import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth Pages
import LoginPage, { RoleSelectPage } from './pages/auth/LoginPage';

// Public marketing pages ("/", "/results")
import LandingPage from './pages/LandingPage';
import StoriesPage from './pages/StoriesPage';
import ResultsPage from './pages/ResultsPage';

// Faculty Pages (existing)
import { 
  DashboardPage, 
  FacultyListPage, 
  CreateFacultyPage, 
  FacultyProfilePage 
} from './pages';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminCreateFacultyPage from './pages/AdminCreateFacultyPage';
import AdminStudentsPage from './pages/AdminStudentsPage';
import AdminStudentLedgerPage from './pages/AdminStudentLedgerPage';
import AdminCreateStudentPage from './pages/AdminCreateStudentPage';
import AdminStudentProfilePage from './pages/AdminStudentProfilePage';
import AdminGroupsPage from './pages/AdminGroupsPage';
import AdminGroupDetailPage from './pages/AdminGroupDetailPage';
import AdminExamsPage from './pages/AdminExamsPage';
import AdminExamDetailPage from './pages/AdminExamDetailPage';
import AdminClassroomPage from './pages/AdminClassroomPage';
import AdminClassroomDetailPage from './pages/AdminClassroomDetailPage';
import AdminAttendancePage from './pages/AdminAttendancePage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminFacultyDrilldownPage from './pages/AdminFacultyDrilldownPage';
import AdminStudentReportPage from './pages/AdminStudentReportPage';
import AdminSchedulePage from './pages/AdminSchedulePage';
import AdminBroadcastPage from './pages/AdminBroadcastPage';
import FacultyBroadcastPage from './pages/FacultyBroadcastPage';

// Faculty Exam Pages
import FacultyExamsPage from './pages/faculty/FacultyExamsPage';
import CreateExamPage from './pages/faculty/CreateExamPage';
import ExamDetailPage from './pages/faculty/ExamDetailPage';
import EvaluationsPage from './pages/faculty/EvaluationsPage';
import EvaluationDetailPage from './pages/faculty/EvaluationDetailPage';
import FacultyStudentLedgerPage from './pages/faculty/FacultyStudentLedgerPage';
import AttendancePage from './pages/faculty/AttendancePage';
import ClassEntriesPage from './pages/faculty/ClassEntriesPage';
import FacultySchedulePage from './pages/faculty/FacultySchedulePage';
import ClassEntryDetailPage from './pages/faculty/ClassEntryDetailPage';
import PublicResultPage from './pages/public/PublicResultPage';
import SharedExamEntryPage from './pages/student/SharedExamEntryPage';

// Student Pages
import StudentDashboard from './pages/student/StudentDashboard';
import StudentExamsPage from './pages/student/StudentExamsPage';
import StudentResultsPage from './pages/student/StudentResultsPage';
import StudentProgressPage from './pages/student/StudentProgressPage';
import ExamPortal from './pages/student/ExamPortal';
import ExamResults from './pages/student/ExamResults';
import StudentProfilePage from './pages/student/StudentProfilePage';
import StudentClassroomPage from './pages/student/StudentClassroomPage';
import NotificationsPage from './pages/NotificationsPage';

const homeRouteForUser = (user) => {
  if (user?.user_type === 'student') return '/student';
  if (user?.user_type === 'admin') return '/admin';
  return '/faculty';
};

// Protected Route Components
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user?.user_type)) {
    return <Navigate to={homeRouteForUser(user)} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    const next = new URLSearchParams(location.search).get('next');
    if (next && user?.user_type === 'student') {
      return <Navigate to={next} replace />;
    }
    return <Navigate to={homeRouteForUser(user)} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      {/* /login is the role-select hub (Student + Faculty tiles only).
          The admin portal lives at a private slug (ADMIN_LOGIN_SLUG in
          LoginPage.js) and is intentionally unlinked — reachable by direct URL
          entry only. See CLAUDE.md "Login routing". */}
      <Route path="/login" element={<PublicRoute><RoleSelectPage /></PublicRoute>} />
      <Route path="/login/:role" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/exam/:examToken" element={<ProtectedRoute allowedTypes={['student']}><SharedExamEntryPage /></ProtectedRoute>} />
      <Route path="/result/:resultToken" element={<ProtectedRoute><PublicResultPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      
      {/* Faculty Routes */}
      <Route path="/faculty" element={<ProtectedRoute allowedTypes={['faculty']}><DashboardPage /></ProtectedRoute>} />
      <Route path="/faculty/members" element={<ProtectedRoute allowedTypes={['faculty']}><FacultyListPage /></ProtectedRoute>} />
      <Route path="/faculty/members/create" element={<ProtectedRoute allowedTypes={['admin']}><CreateFacultyPage /></ProtectedRoute>} />
      <Route path="/faculty/members/:id" element={<ProtectedRoute allowedTypes={['faculty']}><FacultyProfilePage /></ProtectedRoute>} />
      <Route path="/faculty/exams" element={<ProtectedRoute allowedTypes={['faculty']}><FacultyExamsPage /></ProtectedRoute>} />
      <Route path="/faculty/exams/create" element={<ProtectedRoute allowedTypes={['faculty']}><CreateExamPage /></ProtectedRoute>} />
      <Route path="/faculty/exams/:examId" element={<ProtectedRoute allowedTypes={['faculty']}><ExamDetailPage /></ProtectedRoute>} />
      <Route path="/faculty/exams/:examId/evaluations/:attemptId" element={<ProtectedRoute allowedTypes={['faculty']}><EvaluationDetailPage /></ProtectedRoute>} />
      <Route path="/faculty/evaluations" element={<ProtectedRoute allowedTypes={['faculty']}><EvaluationsPage /></ProtectedRoute>} />
      <Route path="/faculty/students/:studentId/ledger" element={<ProtectedRoute allowedTypes={['faculty']}><FacultyStudentLedgerPage /></ProtectedRoute>} />
      <Route path="/faculty/attendance" element={<ProtectedRoute allowedTypes={['faculty']}><AttendancePage /></ProtectedRoute>} />
      <Route path="/faculty/classroom" element={<ProtectedRoute allowedTypes={['faculty']}><ClassEntriesPage /></ProtectedRoute>} />
      <Route path="/faculty/classroom/:entryId" element={<ProtectedRoute allowedTypes={['faculty']}><ClassEntryDetailPage /></ProtectedRoute>} />
      <Route path="/faculty/schedule" element={<ProtectedRoute allowedTypes={['faculty']}><FacultySchedulePage /></ProtectedRoute>} />
      <Route path="/faculty/broadcast" element={<ProtectedRoute allowedTypes={['faculty']}><FacultyBroadcastPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedTypes={['admin']}><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/students" element={<ProtectedRoute allowedTypes={['admin']}><AdminStudentsPage /></ProtectedRoute>} />
      <Route path="/admin/students/new" element={<ProtectedRoute allowedTypes={['admin']}><AdminCreateStudentPage /></ProtectedRoute>} />
      <Route path="/admin/students/:studentId/ledger" element={<ProtectedRoute allowedTypes={['admin']}><AdminStudentLedgerPage /></ProtectedRoute>} />
      <Route path="/admin/students/:studentId" element={<ProtectedRoute allowedTypes={['admin']}><AdminStudentProfilePage /></ProtectedRoute>} />
      <Route path="/admin/exams" element={<ProtectedRoute allowedTypes={['admin']}><AdminExamsPage /></ProtectedRoute>} />
      <Route path="/admin/exams/:examId" element={<ProtectedRoute allowedTypes={['admin']}><AdminExamDetailPage /></ProtectedRoute>} />
      <Route path="/admin/attendance" element={<ProtectedRoute allowedTypes={['admin']}><AdminAttendancePage /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute allowedTypes={['admin']}><AdminReportsPage /></ProtectedRoute>} />
      <Route path="/admin/reports/faculty/:id" element={<ProtectedRoute allowedTypes={['admin']}><AdminFacultyDrilldownPage /></ProtectedRoute>} />
      <Route path="/admin/reports/students/:studentId" element={<ProtectedRoute allowedTypes={['admin']}><AdminStudentReportPage /></ProtectedRoute>} />
      <Route path="/admin/schedule" element={<ProtectedRoute allowedTypes={['admin']}><AdminSchedulePage /></ProtectedRoute>} />
      <Route path="/admin/classroom" element={<ProtectedRoute allowedTypes={['admin']}><AdminClassroomPage /></ProtectedRoute>} />
      <Route path="/admin/classroom/:entryId" element={<ProtectedRoute allowedTypes={['admin']}><AdminClassroomDetailPage /></ProtectedRoute>} />
      <Route path="/admin/broadcast" element={<ProtectedRoute allowedTypes={['admin']}><AdminBroadcastPage /></ProtectedRoute>} />
      <Route path="/admin/groups" element={<ProtectedRoute allowedTypes={['admin']}><AdminGroupsPage /></ProtectedRoute>} />
      <Route path="/admin/groups/:id" element={<ProtectedRoute allowedTypes={['admin']}><AdminGroupDetailPage /></ProtectedRoute>} />
      <Route path="/admin/faculty" element={<ProtectedRoute allowedTypes={['admin']}><FacultyListPage /></ProtectedRoute>} />
      <Route path="/admin/faculty/create" element={<ProtectedRoute allowedTypes={['admin']}><AdminCreateFacultyPage /></ProtectedRoute>} />
      <Route path="/admin/faculty/:id" element={<ProtectedRoute allowedTypes={['admin']}><FacultyProfilePage /></ProtectedRoute>} />
      
      {/* Student Routes */}
      <Route path="/student" element={<ProtectedRoute allowedTypes={['student']}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/exams" element={<ProtectedRoute allowedTypes={['student']}><StudentExamsPage /></ProtectedRoute>} />
      <Route path="/student/results" element={<ProtectedRoute allowedTypes={['student']}><StudentResultsPage /></ProtectedRoute>} />
      <Route path="/student/progress" element={<ProtectedRoute allowedTypes={['student']}><StudentProgressPage /></ProtectedRoute>} />
      <Route path="/student/profile" element={<ProtectedRoute allowedTypes={['student']}><StudentProfilePage /></ProtectedRoute>} />
      <Route path="/student/classroom" element={<ProtectedRoute allowedTypes={['student']}><StudentClassroomPage /></ProtectedRoute>} />
      <Route path="/student/exam/:examId" element={<ProtectedRoute allowedTypes={['student']}><ExamPortal /></ProtectedRoute>} />
      <Route path="/student/exam/:examId/attempt/:attemptId" element={<ProtectedRoute allowedTypes={['student']}><ExamPortal /></ProtectedRoute>} />
      <Route path="/student/results/:attemptId" element={<ProtectedRoute allowedTypes={['student']}><ExamResults /></ProtectedRoute>} />
      
      {/* "/" is the public marketing landing page (was: redirect to /login).
          Wrapped in PublicRoute to match /login's pattern — an already
          authenticated visitor is sent to their role home instead. */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/results" element={<PublicRoute><ResultsPage /></PublicRoute>} />
      <Route path="/stories" element={<PublicRoute><StoriesPage /></PublicRoute>} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              className: 'font-body',
            }}
          />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
